import { plural } from '@cian/utils';
import { isNil } from 'ramda';
import { useSelector } from 'react-redux';

import { IOfferSummaryInfoItemProps } from '../../../../../components/OfferSummaryInfo';
import {
  BuildingType,
  BuildingTypeNames,
  ParkingTypeNames,
  RepairType,
} from '../../../../../components/additional_features/locales';
import { getHeatingType, getOverlapType } from '../../../../../components/bti_house_data/helpers';
import { offerDataSelector, offerSelector } from '../../../../../selectors';
import { getOfferType, isLand, isUrban } from '../../../../../utils/offer_type';

export interface IUseSummaryInfoBuildingPropsResponse {
  rightGroup: boolean;
  subscribeBtn: boolean;
  summaryInfoItems: IOfferSummaryInfoItemProps[];
}

export const useSummaryInfoBuildingProps = (): IUseSummaryInfoBuildingPropsResponse | null => {
  const offer = useSelector(offerSelector);
  const offerData = useSelector(offerDataSelector);

  const offerType = getOfferType(offer);

  const isUrbanOffer = isUrban(offerType);
  const isLandOffer = isLand(offerType);

  const { building } = offer;
  const { bti } = offerData;

  if (!building || isLandOffer) {
    return null;
  }

  const summaryInfoItems: IOfferSummaryInfoItemProps[] = [];

  if (bti?.houseData?.yearRelease) {
    summaryInfoItems.push({
      name: 'Год постройки',
      value: bti.houseData.yearRelease,
    });
  }

  if (bti?.houseData?.seriesName) {
    summaryInfoItems.push({
      name: 'Строительная серия',
      value: bti.houseData.seriesName,
    });
  }

  if (isUrbanOffer) {
    summaryInfoItems.push({
      name: 'Мусоропровод',
      value: building.hasGarbageChute ? 'Есть' : 'Нет',
    });
  }

  if (building && (building.passengerLiftsCount || building.cargoLiftsCount)) {
    const passengerLiftsValue = building.passengerLiftsCount
      ? `${building.passengerLiftsCount} ${plural(building.passengerLiftsCount, [
          'пассажирский',
          'пассажирских',
          'пассажирских',
        ])}`
      : null;

    const cargoLiftsCount = building.cargoLiftsCount
      ? `${building.cargoLiftsCount} ${plural(building.cargoLiftsCount, ['грузовой', 'грузовых', 'грузовых'])}`
      : null;

    const liftsValue = [passengerLiftsValue, cargoLiftsCount].filter(Boolean).join(', ');

    summaryInfoItems.push({
      name: 'Количество лифтов',
      value: liftsValue,
    });
  }

  if (building.statusType) {
    summaryInfoItems.push({
      name: 'Тип недвижимости',
      value: BuildingTypeNames[building.statusType],
    });
  }

  if (building.materialType) {
    summaryInfoItems.push({
      name: 'Тип дома',
      value: BuildingType[building.materialType],
    });
  }

  if (bti?.houseData?.houseOverlapType) {
    summaryInfoItems.push({
      name: 'Тип перекрытий',
      value: getOverlapType(bti.houseData.houseOverlapType) ?? 'Нет информации',
    });
  }

  if (building.parking?.type) {
    summaryInfoItems.push({
      name: 'Парковка',
      value: ParkingTypeNames[building.parking.type],
    });
  }

  if (bti?.houseData?.entrances) {
    summaryInfoItems.push({
      name: 'Подъезды',
      value: bti.houseData.entrances,
    });
  }

  if (bti?.houseData?.houseHeatSupplyType) {
    summaryInfoItems.push({
      name: 'Отопление',
      value: getHeatingType(bti.houseData.houseHeatSupplyType) ?? 'Нет информации',
    });
  }

  if (!isNil(bti?.houseData?.isEmergency)) {
    summaryInfoItems.push({
      name: 'Аварийность',
      value: bti?.houseData?.isEmergency ? 'Да' : 'Нет',
    });
  }

  if (offer.repairType) {
    summaryInfoItems.push({
      name: 'Ремонт',
      value: RepairType[offer.repairType],
    });
  }

  if (!summaryInfoItems.length) {
    return null;
  }

  return {
    rightGroup: isUrbanOffer,
    subscribeBtn: isUrbanOffer,
    summaryInfoItems,
  };
};
